/**
 * Funções puras de formatação/identificação de "Gerenciar Comissões".
 * Mantidas fora do componente (SRP) para orquestração enxuta e teste unitário direto.
 */

/** Chave mínima para identificar um integrante (interno ou de entidade externa). */
export interface IdentificacaoMembro {
    idSolicitante?: number | null;
    idSolicitanteEe?: number | null;
    uf?: string;
    ufSgUfEe?: string | null;
}

/** Converte data brasileira (dd/mm/yyyy) para o formato do backend (yyyy-mm-dd). */
export function formatarDataBackend(data: string): string | null {
    if (!data) {
        return null;
    }
    const partes = data.split('/');
    if (partes.length === 3) {
        const dia = partes[0].padStart(2, '0');
        const mes = partes[1].padStart(2, '0');
        const ano = partes[2];
        if (dia && mes && ano && ano.length === 4) {
            return `${ano}-${mes}-${dia}`;
        }
    }
    return null;
}

/** Converte data do backend (yyyy-mm-dd[THH:mm]) para exibição (dd/mm/yyyy). */
export function formatarDataFrontend(data: string): string {
    if (!data) {
        return '';
    }
    const dataLimpa = data.split('T')[0];
    const partes = dataLimpa.split('-');
    if (partes.length === 3) {
        const [ano, mes, dia] = partes;
        return `${dia}/${mes}/${ano}`;
    }
    return '';
}

/** Identidade estável de um integrante para comparação/seleção. */
export function chaveMembro(membro: IdentificacaoMembro): string {
    const id = membro.idSolicitante || membro.idSolicitanteEe || '';
    const uf = membro.uf || membro.ufSgUfEe || '';
    return `${id}:${uf}`;
}

/** Iniciais (primeira + última) do nome, em maiúsculas. */
export function iniciaisMembro(nome: string): string {
    if (!nome) {
        return '';
    }
    const partes = nome.trim().split(/\s+/);
    if (partes.length === 0) {
        return '';
    }
    if (partes.length === 1) {
        return partes[0].charAt(0).toUpperCase();
    }
    return (partes[0].charAt(0) + partes[partes.length - 1].charAt(0)).toUpperCase();
}

/** UF normalizada (maiúscula, sem espaços) ou "-" quando ausente. */
export function formatarUf(uf?: string | null): string {
    const valor = (uf ?? '').trim().toUpperCase();
    return valor || '-';
}

/** Caminho da bandeira da UF, só para siglas válidas de 2 letras. */
export function obterBandeiraUf(uf?: string | null): string | null {
    const sigla = formatarUf(uf);
    if (!/^[A-Z]{2}$/.test(sigla)) {
        return null;
    }
    return `images/estados/${sigla}.png`;
}

/** Separa e limpa uma lista de e-mails informada como string única. */
export function obterListaEmails(emails?: string): string[] {
    if (!emails) {
        return [];
    }
    return emails
        .split(/[,;\s]+/)
        .map((email) => email.trim())
        .filter((email) => !!email);
}

/** Separa e limpa uma lista de telefones informada como string única. */
export function obterListaTelefones(telefones?: string): string[] {
    if (!telefones) {
        return [];
    }
    return telefones
        .split(/[/,;]+/)
        .map((telefone) => telefone.trim())
        .filter((telefone) => !!telefone);
}
